import { cn } from '@/lib/utils';
import {
    ArrowRightLeft,
    Box,
    Bug,
    Check,
    ChevronDown,
    ChevronRight,
    Copy,
    Fuel,
    Hash,
    Loader2,
    Play,
    RefreshCw,
    Search,
    Trash2,
    User,
} from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

/* ================================================================== */
/*  Types                                                             */
/* ================================================================== */

interface RpcCall {
  id: string;
  method: string;
  params: unknown[];
  result?: unknown;
  error?: string;
  latencyMs: number;
  timestamp: Date;
}

interface BlockInfo {
  number: string;
  hash: string;
  timestamp: string;
  gasUsed: string;
  gasLimit: string;
  transactions: number;
  baseFeePerGas?: string;
}

interface TxInfo {
  hash: string;
  from: string;
  to: string | null;
  value: string;
  gasUsed?: string;
  status?: 'success' | 'failed' | 'pending';
  blockNumber?: string;
  method?: string;
}

/* ================================================================== */
/*  RPC Methods                                                       */
/* ================================================================== */

const COMMON_METHODS = [
  { method: 'eth_blockNumber', params: [], label: 'Block Number' },
  { method: 'eth_chainId', params: [], label: 'Chain ID' },
  { method: 'eth_gasPrice', params: [], label: 'Gas Price' },
  { method: 'net_version', params: [], label: 'Network Version' },
  { method: 'eth_getBalance', params: ['0x...', 'latest'], label: 'Get Balance' },
  { method: 'eth_getBlockByNumber', params: ['latest', false], label: 'Latest Block' },
  { method: 'eth_getTransactionCount', params: ['0x...', 'latest'], label: 'Tx Count (Nonce)' },
  { method: 'eth_estimateGas', params: [{ to: '0x...', data: '0x' }], label: 'Estimate Gas' },
  { method: 'eth_maxPriorityFeePerGas', params: [], label: 'Priority Fee' },
  { method: 'eth_feeHistory', params: ['0x5', 'latest', [25, 50, 75]], label: 'Fee History' },
] as const;

/* ================================================================== */
/*  Helpers                                                           */
/* ================================================================== */

function truncateHash(hash: string, start = 8, end = 6): string {
  if (hash.length <= start + end + 2) return hash;
  return `${hash.slice(0, start)}...${hash.slice(-end)}`;
}

function hexToNumber(hex: string): string {
  try {
    return BigInt(hex).toString();
  } catch {
    return hex;
  }
}

function weiToEth(wei: string): string {
  try {
    const n = BigInt(wei);
    const eth = Number(n) / 1e18;
    return eth.toFixed(6) + ' ETH';
  } catch {
    return wei;
  }
}

function gweiFromWei(wei: string): string {
  try {
    const n = BigInt(wei);
    const gwei = Number(n) / 1e9;
    return gwei.toFixed(2) + ' Gwei';
  } catch {
    return wei;
  }
}

/* ================================================================== */
/*  Component                                                         */
/* ================================================================== */

interface RpcDebuggerPanelProps {
  rpcEndpoint?: string;
}

export default function RpcDebuggerPanel({ rpcEndpoint }: RpcDebuggerPanelProps) {
  const [calls, setCalls] = useState<RpcCall[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState('eth_blockNumber');
  const [customParams, setCustomParams] = useState('');
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'rpc' | 'blocks' | 'transactions'>('rpc');
  const [latestBlock, setLatestBlock] = useState<BlockInfo | null>(null);
  const [recentTxs, setRecentTxs] = useState<TxInfo[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [addressQuery, setAddressQuery] = useState('');
  const callIdRef = useRef(0);

  const defaultRpc = rpcEndpoint || 'http://localhost:8545';

  const makeRpcCall = useCallback(
    async (method: string, params: unknown[]) => {
      const start = performance.now();
      const id = `rpc-${++callIdRef.current}`;

      try {
        setIsLoading(true);
        const resp = await fetch(defaultRpc, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jsonrpc: '2.0', method, params, id: callIdRef.current }),
        });
        const json = await resp.json();
        const latencyMs = Math.round(performance.now() - start);

        const call: RpcCall = {
          id,
          method,
          params,
          result: json.result,
          error: json.error?.message,
          latencyMs,
          timestamp: new Date(),
        };

        setCalls((prev) => [call, ...prev].slice(0, 100));
        return json.result;
      } catch (err: unknown) {
        const latencyMs = Math.round(performance.now() - start);
        const call: RpcCall = {
          id,
          method,
          params,
          error: err instanceof Error ? err.message : String(err),
          latencyMs,
          timestamp: new Date(),
        };
        setCalls((prev) => [call, ...prev].slice(0, 100));
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [defaultRpc],
  );

  const handleQuickCall = useCallback(async () => {
    const preset = COMMON_METHODS.find((m) => m.method === selectedMethod);
    let params: unknown[] = preset ? [...preset.params] : [];
    if (customParams.trim()) {
      try {
        params = JSON.parse(customParams);
      } catch {
        /* keep default */
      }
    }
    await makeRpcCall(selectedMethod, params);
  }, [selectedMethod, customParams, makeRpcCall]);

  const fetchLatestBlock = useCallback(async () => {
    const result = await makeRpcCall('eth_getBlockByNumber', ['latest', true]);
    if (result) {
      setLatestBlock({
        number: hexToNumber(result.number),
        hash: result.hash,
        timestamp: new Date(parseInt(result.timestamp, 16) * 1000).toLocaleTimeString(),
        gasUsed: hexToNumber(result.gasUsed),
        gasLimit: hexToNumber(result.gasLimit),
        transactions: result.transactions?.length ?? 0,
        baseFeePerGas: result.baseFeePerGas ? gweiFromWei(result.baseFeePerGas) : undefined,
      });
      if (result.transactions?.length) {
        const txs: TxInfo[] = result.transactions
          .slice(0, 10)
          .map((tx: Record<string, string>) => ({
            hash: tx.hash,
            from: tx.from,
            to: tx.to,
            value: weiToEth(tx.value),
            blockNumber: hexToNumber(result.number),
          }));
        setRecentTxs(txs);
      }
    }
  }, [makeRpcCall]);

  const copyToClipboard = useCallback((text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  const CopyButton = ({ text, id }: { text: string; id: string }) => (
    <button
      onClick={() => copyToClipboard(text, id)}
      className="p-0.5 text-gray-500 hover:text-white transition-colors"
    >
      {copiedId === id ? (
        <Check className="w-3 h-3 text-green-400" />
      ) : (
        <Copy className="w-3 h-3" />
      )}
    </button>
  );

  return (
    <div className="h-full flex flex-col bg-[#1e1e1e] text-[#cccccc]">
      {/* Header */}
      <div className="flex items-center justify-between h-9 px-3 border-b border-[#2d2d2d] shrink-0">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#bbbbbb]">
          <Bug className="w-3.5 h-3.5 text-orange-400" />
          RPC Debugger
        </div>
        <button
          onClick={() => setCalls([])}
          className="p-1 text-[#858585] hover:text-white transition-colors"
          title="Clear history"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-[#2d2d2d] shrink-0">
        {(['rpc', 'blocks', 'transactions'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider transition-colors',
              activeTab === tab
                ? 'text-white border-b-2 border-blue-500'
                : 'text-[#858585] hover:text-white',
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* RPC endpoint display */}
      <div className="px-3 py-1.5 bg-[#252526] border-b border-[#2d2d2d] text-[10px] text-[#666] flex items-center gap-1.5 shrink-0">
        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
        <span className="truncate">{defaultRpc}</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* RPC Tab */}
        {activeTab === 'rpc' && (
          <div className="p-3 space-y-3">
            {/* Quick call interface */}
            <div className="space-y-2">
              <select
                value={selectedMethod}
                onChange={(e) => setSelectedMethod(e.target.value)}
                className="w-full bg-[#2d2d2d] text-xs text-[#d4d4d4] rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-orange-500 border border-[#444]"
              >
                {COMMON_METHODS.map((m) => (
                  <option key={m.method} value={m.method}>
                    {m.label} — {m.method}
                  </option>
                ))}
              </select>

              <input
                type="text"
                value={customParams}
                onChange={(e) => setCustomParams(e.target.value)}
                placeholder="Custom params (JSON array) — optional"
                className="w-full bg-[#2d2d2d] text-xs text-[#d4d4d4] rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-orange-500 border border-[#444] placeholder-[#666]"
              />

              <button
                onClick={handleQuickCall}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs font-medium bg-orange-600 hover:bg-orange-700 text-white transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Play className="w-3.5 h-3.5" />
                )}
                Send RPC Call
              </button>
            </div>

            {/* Call history */}
            <div className="space-y-1.5">
              <h4 className="text-[10px] text-[#666] uppercase font-semibold tracking-wider">
                History
              </h4>
              {calls.length === 0 ? (
                <p className="text-[10px] text-[#555] italic">No calls yet — run a method above</p>
              ) : (
                calls.map((call) => (
                  <div key={call.id} className="bg-[#252526] border border-[#333] rounded">
                    <button
                      onClick={() => setExpandedCallId(expandedCallId === call.id ? null : call.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-left"
                    >
                      {expandedCallId === call.id ? (
                        <ChevronDown className="w-3 h-3 text-[#666] shrink-0" />
                      ) : (
                        <ChevronRight className="w-3 h-3 text-[#666] shrink-0" />
                      )}
                      <span
                        className={cn(
                          'w-1.5 h-1.5 rounded-full shrink-0',
                          call.error ? 'bg-red-500' : 'bg-green-500',
                        )}
                      />
                      <span className="text-[10px] text-blue-400 font-mono flex-1 truncate">
                        {call.method}
                      </span>
                      <span className="text-[9px] text-[#666] shrink-0">{call.latencyMs}ms</span>
                    </button>
                    {expandedCallId === call.id && (
                      <div className="px-2 pb-2 space-y-1 text-[10px]">
                        <div>
                          <span className="text-[#666]">Params: </span>
                          <code className="text-[#d4d4d4] font-mono">
                            {JSON.stringify(call.params)}
                          </code>
                        </div>
                        {call.result !== undefined && (
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="text-[#666]">Result: </span>
                              <CopyButton
                                text={JSON.stringify(call.result, null, 2)}
                                id={`res-${call.id}`}
                              />
                            </div>
                            <pre className="mt-0.5 text-green-300 font-mono bg-[#1e1e1e] p-1.5 rounded text-[9px] overflow-auto max-h-40">
                              {JSON.stringify(call.result, null, 2)}
                            </pre>
                          </div>
                        )}
                        {call.error && (
                          <div>
                            <span className="text-[#666]">Error: </span>
                            <span className="text-red-400">{call.error}</span>
                          </div>
                        )}
                        <div className="text-[9px] text-[#555]">
                          {call.timestamp.toLocaleTimeString()}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Blocks Tab */}
        {activeTab === 'blocks' && (
          <div className="p-3 space-y-3">
            <button
              onClick={fetchLatestBlock}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Fetch Latest Block
            </button>

            {latestBlock && (
              <div className="bg-[#252526] border border-[#333] rounded p-3 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-medium text-white">
                  <Box className="w-3.5 h-3.5 text-blue-400" />
                  Block #{latestBlock.number}
                </div>
                <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                  <div>
                    <span className="text-[#666]">Hash: </span>
                    <span className="text-[#d4d4d4] font-mono">
                      {truncateHash(latestBlock.hash)}
                    </span>
                    <CopyButton text={latestBlock.hash} id="block-hash" />
                  </div>
                  <div>
                    <span className="text-[#666]">Time: </span>
                    <span className="text-[#d4d4d4]">{latestBlock.timestamp}</span>
                  </div>
                  <div>
                    <span className="text-[#666]">Gas Used: </span>
                    <span className="text-yellow-300">
                      {Number(latestBlock.gasUsed).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#666]">Gas Limit: </span>
                    <span className="text-[#d4d4d4]">
                      {Number(latestBlock.gasLimit).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#666]">Txns: </span>
                    <span className="text-blue-300">{latestBlock.transactions}</span>
                  </div>
                  {latestBlock.baseFeePerGas && (
                    <div>
                      <span className="text-[#666]">Base Fee: </span>
                      <span className="text-green-300">{latestBlock.baseFeePerGas}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!latestBlock && (
              <p className="text-[10px] text-[#555] italic text-center py-4">
                Click "Fetch Latest Block" to view block data
              </p>
            )}
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="p-3 space-y-3">
            {/* Address lookup */}
            <div className="flex gap-1.5">
              <input
                type="text"
                value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                placeholder="Enter tx hash to look up..."
                className="flex-1 bg-[#2d2d2d] text-xs text-[#d4d4d4] rounded px-2 py-1.5 outline-none focus:ring-1 focus:ring-orange-500 border border-[#444] placeholder-[#666] font-mono"
              />
              <button
                onClick={async () => {
                  if (!addressQuery.trim()) return;
                  const result = await makeRpcCall('eth_getTransactionReceipt', [
                    addressQuery.trim(),
                  ]);
                  if (result) {
                    setRecentTxs((prev) => [
                      {
                        hash: result.transactionHash,
                        from: result.from,
                        to: result.to,
                        value: '—',
                        gasUsed: hexToNumber(result.gasUsed),
                        status: result.status === '0x1' ? 'success' : 'failed',
                        blockNumber: hexToNumber(result.blockNumber),
                      },
                      ...prev,
                    ]);
                  }
                }}
                disabled={isLoading}
                className="px-2 py-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded text-xs transition-colors disabled:opacity-50"
              >
                <Search className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Recent Transactions */}
            <h4 className="text-[10px] text-[#666] uppercase font-semibold tracking-wider">
              Recent Transactions
            </h4>
            {recentTxs.length === 0 ? (
              <p className="text-[10px] text-[#555] italic text-center py-4">
                No transactions yet — fetch a block or look up a tx hash
              </p>
            ) : (
              <div className="space-y-1.5">
                {recentTxs.map((tx, i) => (
                  <div
                    key={`${tx.hash}-${i}`}
                    className="bg-[#252526] border border-[#333] rounded p-2 space-y-1"
                  >
                    <div className="flex items-center gap-1.5 text-[10px]">
                      <ArrowRightLeft className="w-3 h-3 text-blue-400 shrink-0" />
                      <span className="font-mono text-blue-300 truncate">
                        {truncateHash(tx.hash)}
                      </span>
                      <CopyButton text={tx.hash} id={`tx-${tx.hash}`} />
                      {tx.status && (
                        <span
                          className={cn(
                            'ml-auto px-1 py-px rounded text-[8px] font-semibold',
                            tx.status === 'success'
                              ? 'bg-green-600/20 text-green-400'
                              : 'bg-red-600/20 text-red-400',
                          )}
                        >
                          {tx.status}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-[9px]">
                      <div className="flex items-center gap-0.5">
                        <User className="w-2.5 h-2.5 text-[#666]" />
                        <span className="text-[#888] font-mono truncate">
                          {truncateHash(tx.from, 6, 4)}
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5">
                        <span className="text-[#666]">→</span>
                        <span className="text-[#888] font-mono truncate">
                          {tx.to ? truncateHash(tx.to, 6, 4) : 'Contract Create'}
                        </span>
                      </div>
                      {tx.value && tx.value !== '—' && (
                        <div className="flex items-center gap-0.5">
                          <Hash className="w-2.5 h-2.5 text-[#666]" />
                          <span className="text-yellow-300">{tx.value}</span>
                        </div>
                      )}
                      {tx.gasUsed && (
                        <div className="flex items-center gap-0.5">
                          <Fuel className="w-2.5 h-2.5 text-[#666]" />
                          <span className="text-[#888]">
                            {Number(tx.gasUsed).toLocaleString()} gas
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
